import {
  ROUTE_EARLY_ACCESS,
  ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD,
  ROUTE_PPRA_PAYMENT,
  ROUTE_SIGN_IN,
} from '@/constants/routes';
import { getRoute, getRouteLoadingScreen } from '@/utils';
import axios, { isAxiosError } from 'axios';
import authService from '../services/auth.service';
import envService from '../services/env.service';

/**
 * Singleton to make requests to the server
 */
const apiClient = axios.create({
  baseURL: envService.getEnv().VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Add a request interceptor to add the token to the headers
 */
apiClient.interceptors.request.use(
  (config) => {
    // Send the authenticated user token if available
    const token = authService.getToken();
    if (token) {
      config.headers['x-authorization'] = `${token}`;
    }

    // Send the visitor session token if available
    const visitorSessionToken = authService.getVisitorToken();
    if (visitorSessionToken) {
      config.headers['x-session-authorization'] = `${visitorSessionToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error === undefined) {
      return Promise.reject('Unknown error');
    }
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }

    const isRouteGetStartedToPpra = envService.getEnv().VITE_SWITCH_ROUTE_GET_STARTED_TO_PPRA === 'true';
    if (error?.response?.status === 401 && error?.response?.data?.message !== 'Session unauthorized') {
      authService.logout();
      window.location.href = getRoute(ROUTE_SIGN_IN);
      return;
    } else if (error?.response?.status === 401 && error?.response?.data?.message === 'Session unauthorized') {
      authService.logoutVisitor();
      return;
    } else if (error?.response?.status === 402) {
      authService.logout();
      window.location.href = getRoute(
        isRouteGetStartedToPpra ? ROUTE_PPRA_PAYMENT : ROUTE_PAYMENT_COLLECT_PAYMENT_METHOD
      );
      return;
    } else if (error?.response?.status === 403 && error.response.data.title === 'Invite required') {
      authService.logout();
      window.location.href = getRoute(isRouteGetStartedToPpra ? ROUTE_PPRA_PAYMENT : ROUTE_EARLY_ACCESS);
      return;
    } else if (error?.response?.status === 405 && error?.response?.data.title === 'Risk not processed') {
      const path = window.location.pathname;
      if (path.split('/')[1] !== 'loading-screen') window.location.href = getRouteLoadingScreen(path);
      return;
    }

    return Promise.reject(error);
  }
);

export default apiClient;
